import type { ActivityTreeNodeFormValues } from '@/features/project/projects/project-detail/activities/_components/ActivityTreeNodeForm';
import { useId } from '@/hooks';
import type { fundSheetTypeEnum } from '@/utils/enum';
import {
  type ContextType,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { z } from 'zod';
import type { fundBusinessForm } from '../../_models/fund-business';
import type { PaymentType } from '../../_models/payment';

type DrawerState =
  | { open: false }
  | { open: true; type: 'ownership-ratio' }
  | { open: true; type: 'inspection-evaluation' }
  | { open: true; type: 'fund-additional-attributes' }
  | { open: true; type: 'fund-investments' }
  | {
      open: true;
      type:
        | 'fund-additional-income-and-expenditure'
        | 'estimated-fund-additional-income-and-expenditure'
        | 'estimated-fund-additional-income-and-expenditure-next-year';
      projectId: string;
    }
  | { open: true; type: 'related-material'; isEditing: boolean }
  | { open: true; type: 'others'; isEditing: boolean }
  | { open: true; type: 'activity'; isEditing: false }
  | { open: true; type: 'activity'; isEditing: true; activityId: string }
  | { open: true; type: 'activity-indicator-form'; isEditing: false }
  | {
      open: true;
      type: 'activity-indicator-form';
      isEditing: true;
      indicatorId: string;
    }
  | {
      open: true;
      type: 'activity-consecutive-indicator-form';
      isEditing: false;
    }
  | {
      open: true;
      type: 'activity-consecutive-indicator-form';
      isEditing: true;
      indicatorId: string;
    }
  | { open: true; type: 'activity-tree-node' }
  | { open: true; type: 'activity-tree-node-form'; isEditing: false }
  | {
      open: true;
      type: 'activity-tree-node-form';
      isEditing: true;
      edgeId: string;
      formValues: ActivityTreeNodeFormValues;
    }
  | { open: true; type: 'cabinet-decisions' }
  | {
      open: true;
      type: 'expenditure-cart';
      isEditing: false;
      subProjectId: string | undefined;
    }
  | {
      open: true;
      type: 'expenditure-cart';
      isEditing: true;
      subProjectId: string;
      expenditureCartId: string;
    }
  | {
      open: true;
      type: 'expenditure-item';
      isEditing: false;
      mode: 'Biaya' | 'Belanja';
      id?: never;
    }
  | {
      open: true;
      type: 'expenditure-item';
      isEditing: true;
      mode: 'Biaya' | 'Belanja';
      id: string;
    }
  | {
      open: true;
      type: 'other-notes';
    }
  | {
      open: true;
      type: 'prior-execution-item';
    }
  | {
      open: true;
      type: 'prior-reserve-budget-item';
    }
  | {
      open: true;
      type: 'execution-item';
      isEditing: true;
      subProjectId: string;
      expenditureCartId: string;
      executionItemId: string;
    }
  | {
      open: true;
      type: 'indirect-costs';
    }
  | {
      open: true;
      type: 'payment-group';
      isEditing: false;
    }
  | {
      open: true;
      type: 'payment-group';
      isEditing: true;
      paymentGroupId: string;
    }
  | {
      open: true;
      type: 'contractor-payment';
      isEditing: false;
    }
  | {
      open: true;
      type: 'contractor-payment';
      isEditing: true;
      paymentId: string;
    }
  | {
      open: true;
      type: 'payee-payment';
      isEditing: false;
      paymentGroupId: string;
    }
  | {
      open: true;
      type: 'payee-payment';
      isEditing: true;
      paymentGroupId: string;
      paymentId: string;
    }
  | {
      open: true;
      type: 'payment-contract';
      isEditing: false;
      paymentGroupId: string;
      paymentId: string;
      paymentType: PaymentType;
    }
  | {
      open: true;
      type: 'payment-contract';
      isEditing: true;
      paymentGroupId: string;
      paymentId: string;
      paymentContractId: string;
      paymentType: PaymentType;
    }
  | {
      open: true;
      type: 'payment-edge';
      isEditing: false;
    }
  | {
      open: true;
      type: 'payment-edge';
      isEditing: true;
      paymentEdgeId: string;
    }
  | {
      open: true;
      type: 'fund-business';
      isEditing: false;
      businessForm?: z.infer<typeof fundBusinessForm>;
      fundSheetType: z.infer<typeof fundSheetTypeEnum>;
    }
  | {
      open: true;
      type: 'fund-business';
      isEditing: true;
      fundBusinessId?: string;
    }
  | {
      open: true;
      type: 'execution-progress';
      formType: 'fund-info';
      isEditing: false;
    }
  | {
      open: true;
      type: 'execution-progress';
      formType: 'fund-info';
      isEditing: true;
      subProjectId: string;
    }
  | {
      open: true;
      type: 'execution-progress';
      formType: 'fund-business' | 'income-and-expenditure';
      subProjectId: string;
    }
  | {
      open: true;
      type: 'execution-progress-income-and-expenditure-summary';
    }
  | {
      open: true;
      type: 'divergence';
    }
  | {
      open: true;
      type: 'ss-previous-fiscal-year-executed';
    }
  | {
      open: true;
      type: 'ss-current-finalized-amount';
    }
  | {
      open: true;
      type: 'alur-dana';
      alurDanaId?: never;
    }
  | {
      open: true;
      type: 'alur-dana';
      alurDanaId: string;
    };

const Context = createContext<
  | {
      drawerState: DrawerState;
      setDrawerState: Dispatch<SetStateAction<DrawerState>>;
    }
  | undefined
>(undefined);

const _useContext = (): NonNullable<ContextType<typeof Context>> => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useDrawerState must be used within DrawerStateProvider.');
  }
  return context;
};

export const useProjectDetailDrawerState = () => {
  return _useContext();
};

export const ProjectDrawerStateProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { id } = useId();
  const prevProjectId = useRef<string | undefined>(undefined);
  const [drawerState, setDrawerState] = useState<DrawerState>({ open: false });

  // 年度切り替え時にドロワーを閉じるため
  useEffect(() => {
    if (id && prevProjectId.current !== id) {
      prevProjectId.current = id;
      setDrawerState({ open: false });
    }
  }, [id]);

  return (
    <Context.Provider value={{ drawerState, setDrawerState }}>
      {children}
    </Context.Provider>
  );
};
